import {
  fetchLlmGatewayConfiguration,
  fetchLlmGatewayKeys,
  fetchLlmGatewayModels,
  formatCurrency,
  selectLlmGatewayConfiguration,
  selectLlmGatewayKeysAboveSpendLimit,
  updateLlmGatewayConfiguration,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { llmGatewayKeysAboveSpendLimitUrl, llmGatewayUrl } from "@app/routes";
import type {
  DeployLlmGatewayConfiguration,
  DeployLlmGatewayModel,
} from "@app/types";
import { useDebouncedValue } from "@app/ui/hooks";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  CheckBox,
  FormGroup,
  FormGroupFeedback,
  Group,
  InputCurrency,
  LoadingSpinner,
  tokens,
} from "../shared";
import { LlmGatewayModelTable } from "../shared/llm-gateway-model/llm-gateway-model-table";

const MAX_SPEND_LIMIT = 10_000;
const DEFAULT_SPEND_LIMIT = 5_000;
const CONFIRMATION_DELAY_MS = 250;

const FormActions = ({
  isLoading,
  onCancel,
  disabled = false,
}: {
  isLoading: boolean;
  onCancel: () => void;
  disabled?: boolean;
}) => (
  <>
    <div className="py-8">
      <hr />
    </div>
    <Group variant="horizontal" size="sm">
      <Button type="submit" isLoading={isLoading} disabled={disabled}>
        Save Changes
      </Button>
      <Button variant="white" onClick={onCancel} disabled={isLoading}>
        Cancel
      </Button>
    </Group>
  </>
);

const SpendSection = ({
  config,
}: { config: DeployLlmGatewayConfiguration }) => {
  const dispatch = useDispatch();
  const [spendLimit, setSpendLimit] = useState<number | undefined>(
    config.spendLimit || DEFAULT_SPEND_LIMIT,
  );
  const [error, setError] = useState<string | undefined>();
  const orgId = useSelector(selectOrganizationSelectedId);

  const action = updateLlmGatewayConfiguration({
    orgId,
    id: config.id,
    spendLimit,
  });
  const loader = useLoader(action);

  const keysLoader = useQuery(fetchLlmGatewayKeys({ orgId }));
  // A changed limit can't be checked against keys that haven't loaded.
  const isCheckingKeys =
    spendLimit !== config.spendLimit && keysLoader.lastSuccess === 0;
  // Trails the field so the warning doesn't flash while the amount is typed.
  const settledLimit = useDebouncedValue(spendLimit, CONFIRMATION_DELAY_MS);
  const loweredKeys = useSelector((s) =>
    selectLlmGatewayKeysAboveSpendLimit(s, { orgId, spendLimit: settledLimit }),
  );

  // The limit that was acknowledged, so editing it again asks again.
  const [confirmedLimit, setConfirmedLimit] = useState<number | undefined>();
  const keysLoweredNow = useSelector((s) =>
    selectLlmGatewayKeysAboveSpendLimit(s, { orgId, spendLimit }),
  );
  // The stored keys keep their old limits until the refetch after a save, so a
  // limit that's already saved shouldn't ask again.
  const isAwaitingConfirmation =
    keysLoweredNow.length > 0 &&
    spendLimit !== config.spendLimit &&
    confirmedLimit !== spendLimit;

  // The API lowers keys above the new limit, so the stored ones are stale.
  useLoaderSuccess(loader, () => {
    setConfirmedLimit(undefined);
    dispatch(fetchLlmGatewayKeys({ orgId }));
  });

  useEffect(() => {
    setSpendLimit(config.spendLimit || DEFAULT_SPEND_LIMIT);
  }, [config.spendLimit]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isAwaitingConfirmation || isCheckingKeys) return;

    if (!spendLimit) {
      setError("Global spend limit is required.");
      return;
    }
    if (spendLimit > MAX_SPEND_LIMIT) {
      setError(
        `Global spend limit cannot exceed ${formatCurrency(MAX_SPEND_LIMIT, 0)}.`,
      );
      return;
    }

    setError(undefined);
    dispatch(action);
  };

  const onCancel = () => {
    setError(undefined);
    setSpendLimit(config.spendLimit || DEFAULT_SPEND_LIMIT);
    setConfirmedLimit(undefined);
  };

  return (
    <Box>
      <h3 className={tokens.type.h3}>Spend</h3>
      <p className={`${tokens.type.darker} mt-2 mb-4`}>
        The global spend limit always takes priority.{" "}
        <strong>Once it's reached, all key usage is blocked</strong> — even if
        individual key limits haven't been hit. Usage resumes when the cycle
        resets or you raise the global limit.
      </p>

      <form onSubmit={onSubmit} noValidate>
        <BannerMessages className="mb-4" {...loader} />

        <FormGroup
          label="Global Spend Limit (required)"
          htmlFor="global-spend-limit"
          feedbackMessage={error}
          feedbackVariant={error ? "danger" : "info"}
        >
          <InputCurrency
            id="global-spend-limit"
            name="global-spend-limit"
            value={spendLimit}
            onChange={setSpendLimit}
          />
        </FormGroup>

        {isCheckingKeys && !keysLoader.isError ? (
          <FormGroupFeedback variant="info">
            Checking which keys this affects…
          </FormGroupFeedback>
        ) : null}
        {isCheckingKeys && keysLoader.isError ? (
          <Banner className="mt-4" variant="error">
            We couldn't load the keys, so the global spend limit can't be
            changed right now.
          </Banner>
        ) : null}

        {loweredKeys.length > 0 &&
        settledLimit !== undefined &&
        settledLimit !== config.spendLimit ? (
          <div className="mt-4">
            <CheckBox
              checked={confirmedLimit === settledLimit}
              onChange={(e) =>
                setConfirmedLimit(
                  e.currentTarget.checked ? settledLimit : undefined,
                )
              }
              labelClassName="ml-3 text-red-500"
              label={
                <>
                  I understand that by lowering the global spend limit to{" "}
                  {formatCurrency(settledLimit)}, the spend limits of{" "}
                  <Link
                    to={llmGatewayKeysAboveSpendLimitUrl(settledLimit)}
                    target="_blank"
                    className="underline"
                  >
                    {loweredKeys.length}{" "}
                    {loweredKeys.length === 1 ? "key" : "keys"}
                  </Link>{" "}
                  will be lowered to match, and raising it again won't restore
                  them.
                </>
              }
            />
          </div>
        ) : null}

        <FormActions
          isLoading={loader.isLoading}
          onCancel={onCancel}
          disabled={isAwaitingConfirmation || isCheckingKeys}
        />
      </form>
    </Box>
  );
};

const ANY_MODEL = "*";

const ModelAccessSection = ({
  config,
}: { config: DeployLlmGatewayConfiguration }) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const savedRestrictsModels =
    config.models.length > 0 && !config.models.includes(ANY_MODEL);
  const [restrictModels, setRestrictModels] = useState(savedRestrictsModels);

  // Dropping the wildcard keeps a restricted save from disabling enforcement.
  const savedModels = config.models.filter((model) => model !== ANY_MODEL);
  const [models, setModels] = useState(savedModels);
  const [error, setError] = useState<string | undefined>();

  // The grid owns the selection, so redrawing checkboxes means remounting.
  const [tableKey, setTableKey] = useState(0);
  const remountTable = () => setTableKey((prev) => prev + 1);

  useQuery(fetchLlmGatewayModels());
  const modelsLoader = useLoader(fetchLlmGatewayModels);
  const allModels: DeployLlmGatewayModel[] = modelsLoader.meta.models || [];

  const isCatalogLoading =
    modelsLoader.isLoading || modelsLoader.isInitialLoading;
  const canEditPolicy = allModels.length > 0;

  const action = updateLlmGatewayConfiguration({
    orgId,
    id: config.id,
    models: restrictModels ? models : [ANY_MODEL],
  });
  const loader = useLoader(action);

  const isRowSelected = (model: DeployLlmGatewayModel) =>
    models.includes(model.id);

  const onToggleRestriction = (checked: boolean) => {
    setRestrictModels(checked);
    setError(undefined);
    remountTable();
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (restrictModels && models.length === 0) {
      setError(
        "Select at least one model, or uncheck the restriction to allow all models.",
      );
      return;
    }

    setError(undefined);
    dispatch(action);
  };

  const onCancel = () => {
    setRestrictModels(savedRestrictsModels);
    setModels(savedModels);
    setError(undefined);
    remountTable();
  };

  return (
    <Box>
      <h3 className={tokens.type.h3}>Model Access</h3>
      <p className={`${tokens.type.darker} mt-2 mb-4`}>
        Define a model allowlist for your organization. Further restrictions
        and/or model fallback behavior can be defined on a per-key basis via the
        keys’ settings pages.
      </p>

      <form onSubmit={onSubmit}>
        <BannerMessages className="mb-4" {...loader} />

        {!isCatalogLoading && !canEditPolicy ? (
          <Banner className="mb-4" variant="error">
            We couldn't load the list of supported models, so model access can't
            be changed right now.
          </Banner>
        ) : null}

        <div className="mb-4">
          <CheckBox
            label="Restrict which models can be used by keys in my organization"
            checked={restrictModels}
            disabled={!canEditPolicy}
            onChange={(e) => onToggleRestriction(e.currentTarget.checked)}
          />
        </div>

        <LlmGatewayModelTable
          key={tableKey}
          rowData={allModels}
          isLoading={modelsLoader.isInitialLoading}
          rowSelection={restrictModels ? "multiRow" : undefined}
          isRowSelected={isRowSelected}
          onSelectionChanged={(rows) => setModels(rows.map((row) => row.id))}
        />

        {error ? (
          <FormGroupFeedback variant="danger">{error}</FormGroupFeedback>
        ) : null}

        <FormActions
          isLoading={loader.isLoading}
          onCancel={onCancel}
          disabled={!canEditPolicy}
        />
      </form>
    </Box>
  );
};

export const TeamManagedAiPage = () => {
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  const { isInitialLoading } = useLoader(fetchLlmGatewayConfiguration);
  const config = useSelector(selectLlmGatewayConfiguration);

  return (
    <Group>
      <h2 className={tokens.type.h2}>Managed AI Settings</h2>

      {isInitialLoading ? <LoadingSpinner show /> : null}

      {!isInitialLoading && !config ? (
        <Banner variant="info">
          This organization isn't enrolled in the LLM Gateway yet.{" "}
          <Link to={llmGatewayUrl()} className="underline">
            Get started
          </Link>{" "}
          to configure spend and model access.
        </Banner>
      ) : null}

      {config ? (
        <>
          <SpendSection config={config} />
          <ModelAccessSection config={config} />
        </>
      ) : null}
    </Group>
  );
};
